import { AllModules } from '@ag-grid-enterprise/all-modules';
import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { select, Store } from '@ngrx/store';
import { CreateRuleEntryInput } from 'app/api/API.service';
import { CompanySubset } from 'app/api/company.api.service';
import { CubeRow } from 'app/main/cube/cube.component';
import { selectCubeForAccount7, selectCubeLoading } from 'app/main/cube/cube.selectors';
import { RuleEntryCreateRequested } from 'app/main/rule/rule.actions';
import { AppState } from 'app/reducers';
import { agGridLocale, EurCurrencyCellRenderer } from 'app/utils/ag-grid.locale';
import { AmplifyService } from 'aws-amplify-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-customer-breakdown',
    templateUrl: './customer-breakdown.component.html',
    styleUrls: ['./customer-breakdown.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None
})
export class CustomerBreakdownComponent implements OnInit, OnDestroy, AfterViewInit {
    public gridApi;
    public gridColumnApi;

    public columnDefs;
    public defaultColDef;
    public autoGroupColumnDef;
    public sideBar;
    public statusBar;
    public rowBuffer;
    public localeText;
    public rowClassRules;
    public pivotMode;
    public modules;
    public popupParent;

    // Route data
    public pageTitle;

    private _search = '';

    @Input() company: CompanySubset;
    @Input() 
    set search(value: string) {
        this._search = value;
        if (this.gridApi) {
            this.gridApi.setQuickFilter(value);      
        }
    }
    get search(): string {
        return this._search;
    }

    @Output() ruleCreated = new EventEmitter();

    keyUp = new Subject<KeyboardEvent>();
    rowData$: Observable<CubeRow[]>;
    loading$: Observable<boolean>;
    selectedRow$: BehaviorSubject<CubeRow>;

    gridViewCtrl: FormControl;

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;
    
    constructor(
        @Inject(DOCUMENT) private document: any,
        protected amplifyService: AmplifyService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected store: Store<AppState>,
        public dialog: MatDialog
    ) {

        this.logger = this.amplifyService.logger('CustomerBreakdownComponent');
        this._unsubscribeAll = new Subject();
        this.selectedRow$ = new BehaviorSubject(null);
        this.modules = AllModules;
        this.pageTitle = activatedRoute.snapshot.data['title'];
        this.gridViewCtrl = new FormControl();
        this.popupParent = document.getElementById('body');
        this.localeText = agGridLocale.fr;

        this.setColumnDefs();
        this.setDefaultColDef();
        this.autoGroupColumnDef = {
            width: 360,
            pinned: 'left'
        };

        this.sideBar = null;
        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left'
                },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' }
            ]
        };
        this.pivotMode = false;
    }

    ngOnInit(): void {
        this.loading$ = this.store.pipe(select(selectCubeLoading));

        this.keyUp
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((event: any) => event.target.value),
                debounceTime(250),
                distinctUntilChanged(),
                tap(search => this.gridApi.setQuickFilter(search))
            )
            .subscribe();

        this.rowData$ = this.store.pipe(select(selectCubeForAccount7));
    }

    ngAfterViewInit(): void { }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    onGridReady(params): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        this.gridApi.setQuickFilter(this._search);
    }

    onSelectionChanged(): void {
        const selectedRows = this.gridApi.getSelectedRows();
        if (selectedRows.length === 1) {
          this.selectedRow$.next(selectedRows[0]);
        } else {
          this.selectedRow$.next(null);
        }
    }

    onCreateRule(): void {
        const target = isNaN(Number(this.search)) ? 'entryLabel' : 'account';
        const input = {
            companyId: this.company.id,
            axis: 'cust',
            name: this.search.toUpperCase(),
            target,
        } as CreateRuleEntryInput;
        this.store.dispatch(new RuleEntryCreateRequested({ input }));
        this.ruleCreated.emit(input);
    }

    public clearSearch(): void {
        this.gridApi.setQuickFilter('');
    }

    onFirstDataRendered(params): void {
        this.autoSizeAll(params);
    }

    private autoSizeAll(params): void {
        const allColumnIds = params.columnApi
            .getAllColumns()
            .map(column => column.colId);
        setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
    }

    private setColumnDefs(): void {
      this.columnDefs = [
        { headerName: 'ANA2', field: 'ana2Name' },
        { headerName: 'ANA3', field: 'ana3Name' },
        { headerName: 'Journal', field: 'journal' },
        { headerName: 'Compte', field: 'account' },
        { headerName: 'Lib Compte', field: 'accountLabel' },
        { headerName: 'Date', field: 'entryDate' },
        { headerName: 'Libellé', field: 'entryLabel', editable: true },
        {
            headerName: 'Flux',
            field: 'flux',
            width: 160,
            cellStyle: { 'text-align': 'right' },
            cellClass: params => {
                return params.data && params.data.flux < 0 ? 'negative-flux' : null;
            },
            cellRenderer: EurCurrencyCellRenderer
        },
        {
            headerName: 'Client',
            field: 'customerLabel',
            cellClass: params => {
                return params.data && params.data.missingRule ? 'missing-rule' : null;
            },
        },
        { headerName: 'Catégorie', field: 'customerCategory' },
      ];
    }

    private setDefaultColDef(): void {
      this.defaultColDef = {
        enableValue: false,
        enableRowGroup: false,
        enablePivot: false,
        sortable: true,
        filter: true,
        resizable: true,
        enableCharts: false
      };
    }

}
